import { t } from "@travily/i18n";
import type CelebrationsRow from "@travily/types/models/CelebrationsRow";
import { useClampedText } from "@ui/hooks/use-clamped-text";
import { formatDate } from "@ui/lib/format-date";
import { tv } from "@ui/lib/tv";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

const celebrationCard = tv({
  slots: {
    root: "relative pb-2 lg:pb-4",
    wrapper: "flex",
    card: [
      "relative z-10 rounded-3xl bg-yellow-50 p-6",
      "shadow-[inset_0px_-1px_0px_0px_#e2d5b5,inset_0px_0px_0px_1px_#eee2c4]",
    ],
    quoteText: "whitespace-pre-line text-sm text-yellow-900 leading-5 lg:text-base lg:leading-6",
    collapsed: "relative overflow-hidden",
    expanded: "",
    toggle: "cursor-pointer font-medium text-yellow-700",
    overlay: [
      "absolute right-9 bottom-0 pl-16 lg:right-[84px]",
      "bg-gradient-to-r from-transparent via-[20%] via-yellow-50 to-yellow-50",
    ],
    attribution: "mt-2 flex items-center gap-3",
    meta: "text-sm text-yellow-800 leading-5 lg:text-base lg:leading-6",
    dot: "size-[3px] shrink-0 rounded-full bg-yellow-800",
    float: [
      "float-right [height:100%]",
      "w-8 [shape-margin:2px] [shape-outside:inset(calc(100%-2lh)_0_0)]",
      "lg:w-20 lg:[shape-margin:6px] lg:[shape-outside:inset(calc(100%-2.5lh)_0_0)]",
    ],
    imageRotate: "rotate-[10deg]",
    imageWrapper: [
      "relative flex items-center justify-center overflow-hidden bg-white",
      "size-16 p-1 md:rounded-[28px] lg:size-28 lg:p-2.5",
      "shadow-card",
    ],
    imageWrapperBorder:
      "pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_#e2d5b5,inset_0px_0px_0px_1px_#eee2c4]",
    clipPosition: "absolute -top-3 -right-[6px] z-30 lg:-top-5 lg:-right-[14.5px]",
    avatarAbsolute: "absolute -right-1 -bottom-1 z-20 lg:-right-[3px] lg:-bottom-[5px]",
  },
});

const LINES = 6;

function PaperclipIcon() {
  return (
    <>
      <svg aria-hidden="true" className="h-[33px] w-[26px] lg:hidden" fill="none" viewBox="0 0 26 33">
        <path
          clipRule="evenodd"
          d="M20.6551 2.3917C19.6461 1.75618 18.4178 1.39224 17.2036 1.54133C16.7647 1.59526 16.3371 1.71751 15.9303 1.90883L15.9293 3.70869C16.3791 3.31009 16.8802 3.10848 17.3882 3.04603C18.1873 2.94787 19.0695 3.1854 19.847 3.67516C20.6246 4.165 21.2363 4.8699 21.5276 5.62111C21.8111 6.35249 21.7958 7.128 21.3383 7.86934L9.40134 27.2132C8.37956 28.9455 7.24381 29.7337 6.23644 29.9765C5.23066 30.2188 4.225 29.9491 3.38428 29.3135C1.68108 28.0254 0.809009 25.357 2.23796 23.0038L11.5235 11.3039C11.7839 10.9758 11.729 10.4984 11.4009 10.238C11.0728 9.97774 10.5954 10.0325 10.335 10.3605L1.01699 22.1L0.989249 22.1357L0.965658 22.1739C-0.950074 25.2786 0.209495 28.8152 2.46874 30.5237C3.61315 31.3891 5.07739 31.8163 6.59205 31.4513C8.00762 31.1102 9.35462 30.1045 10.4781 28.3562L22.6289 8.66579C23.3644 7.47403 23.3765 6.19432 22.9421 5.0733C22.5154 3.97251 21.6639 3.02723 20.6551 2.3917Z"
          fill="#FBC852"
          fillRule="evenodd"
        />
      </svg>
      <svg aria-hidden="true" className="hidden h-[57px] w-[44px] lg:block" fill="none" viewBox="0 0 44 57">
        <path
          clipRule="evenodd"
          d="M35.5723 3.84878C33.9129 2.80143 31.8698 2.25183 29.8295 2.59431C28.5659 2.80647 27.3575 3.35404 26.2863 4.26355L26.2861 8.31417C26.4501 8.20873 26.5945 8.06721 26.7037 7.89027C27.742 6.20793 29.0303 5.45828 30.2751 5.24925C31.5565 5.03415 32.9373 5.36966 34.1351 6.12566C36.589 7.67452 37.777 10.5538 36.2013 13.1074L15.3126 46.9574C13.5051 50.0099 11.5725 51.4366 9.91276 51.9161C8.27809 52.3883 6.71224 52.0021 5.43633 51.0196C2.8471 49.0256 1.50965 44.5737 4.06448 40.38L20.0927 19.7637C20.5491 19.1768 20.4431 18.331 19.8562 17.8746C19.2692 17.4185 18.4234 17.5243 17.9671 18.1112L1.88593 38.7947L1.84133 38.852L1.80286 38.9144C-1.50261 44.2713 0.0771878 50.2904 3.79326 53.1523C5.67504 54.6014 8.11653 55.2377 10.6595 54.5033C13.0155 53.8227 15.2827 52.0184 17.231 48.9755L38.4925 14.5213C41.0919 10.309 38.8328 5.90676 35.5723 3.84878Z"
          fill="#FBC852"
          fillRule="evenodd"
        />
      </svg>
    </>
  );
}

export function CelebrationCard({ celebration }: { celebration: CelebrationsRow }) {
  const styles = celebrationCard();
  const authorName = celebration.author_name ?? "";
  const { collapse, expand, expanded, isClamped, ref, style } = useClampedText({ lines: LINES });

  const quoteText = `\u201C${celebration.body}\u201D`;
  const authorAccount = {
    profile_type: celebration.author_profile_type ?? "Family",
    avatar: celebration.author_avatar,
    initial: celebration.author_initial,
  };

  return (
    <div className={styles.root()}>
      <div className={styles.card()}>
        <div className={styles.wrapper()}>
          {expanded ? (
            <div>
              <div className={styles.float()} aria-hidden="true" />
              <p className={styles.quoteText()}>
                {quoteText}{" "}
                <button className={styles.toggle()} onClick={collapse} type="button">
                  {t("common.actions.view_less")}
                </button>
              </p>
            </div>
          ) : (
            <div ref={ref} className={styles.collapsed()} style={style}>
              <div className={styles.float()} aria-hidden="true" />
              <p className={styles.quoteText()}>{quoteText}</p>
              {isClamped && (
                <span className={styles.overlay()}>
                  {/* eslint-disable-next-line i18next/no-literal-string -- typographic ellipsis, not translatable */}
                  {"... "}
                  <button className={styles.toggle()} onClick={expand} type="button">
                    {t("common.actions.view_more")}
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
        <div className={styles.attribution()}>
          <span className={styles.meta()}>{authorName}</span>
          <span className={styles.dot()} aria-hidden="true" />
          <span className={styles.meta()}>
            {/* eslint-disable-next-line i18next/no-literal-string -- Intl.DateTimeFormat option values */}
            {formatDate(celebration.created_at, { month: "long", year: "numeric" })}
          </span>
        </div>
      </div>
      <div className={styles.avatarAbsolute()}>
        <div className={styles.imageRotate()}>
          <div className={styles.imageWrapper()}>
            <AccountAvatar account={authorAccount} className="rounded-[11px] lg:hidden" shape="square" size="52" />
            <AccountAvatar account={authorAccount} className="hidden rounded-3xl lg:flex" shape="square" size="92" />
            <div className={styles.imageWrapperBorder()} />
          </div>
        </div>
        <div className={styles.clipPosition()}>
          <PaperclipIcon />
        </div>
      </div>
    </div>
  );
}
